import React, {
    useContext,
    useEffect,
    useMemo,
    useState,
    useCallback,
} from 'react';
import { Image, Modal, Text } from '@ui/atoms';
import Button from '@ui/atoms/design-system/Button';
import { buttonSizes, buttonTypes } from '@ui/atoms/utils/enums';
import AuthContext from '@context/auth';
import useAuthStore from '@store/useAuthStore';

import KeyAttributes from '@components/BondDirectoryFeature/feature/BondDetails/components/KeyAttributes';
import DealDetails from '@components/BondDirectoryFeature/feature/BondDetails/components/DealDetails';
import RiskAssessment from '@components/BondDirectoryFeature/feature/BondDetails/components/RiskAssessment';
import ContactDetails from '@components/BondDirectoryFeature/feature/BondDetails/components/Contact';
import BondDirectoryOpportunityCarousel from '@components/BondDirectoryFeature/components/BondDirectoryOpportunityCarousel';
import RepaymentScheduleModal from '@components/BondDirectoryFeature/feature/BondDetails/components/RepaymentScheduleModal';

import {
    determineAction,
    getDecimalNumberWithComma,
    triggerCustomGaEvent,
} from '@helpers/miscelleanous';
import { cn } from '@ui/atoms/utils/utils';
import {
    activeOpportunityListingGaEventsName,
    bondDirectoryGaEventsName,
} from '@helpers/ga-events-name-enum';
import texts from '@components/BondDirectoryFeature/feature/BondDetails/en.json';
import {
    logEvent,
    moengageEventConstants,
    moengageEventArgsConstants,
} from '@services/moengage';
import { createDealInterest } from '@services/bond-details.service';
import { useStoreSelector } from '@ui/hooks/useStoreSelector';
import { isValidResponseEntity } from '@helpers/utils';
import { useRouter } from 'next/router';
import { copyToClipboard } from '@ui/atoms/utils/helper';
import ListingWrapper from '@components/OpportunityListing/components/ListingWrapper';
import OpportunityDetailsHeader from '@components/BondDirectoryFeature/feature/BondDetails/components/OpportunityDetailsHeader';

import PricingMasked from './components/PricingMasked';
import DocumentsMasked from './components/DocumentsMasked';
import PopularTag from '@components/OpportunityListing/components/OpporunityCard/PopularTag';
import KeyInvestmentHighlightsMask from './components/KeyInvestmentHighlightsMask';
import ScrollableTabs from '@molecules/ScrollableTabs';
import TabSection from '@atoms/TabSection';
import useWindowDimensions from '@hooks/useWindowDimensions';
import LoginCTA from './components/LoginCTA';
import { getReferralCode } from '@services/identity.service';

const BondDetailsView = ({ id, bondDetailsResponse, isFromGoogle }) => {
    const { headerDetails } = bondDetailsResponse;
    const { title, tags, details, logo } = headerDetails || {};
    const router = useRouter();
    const { width } = useWindowDimensions();
    const hasPaymentSchedule = !!bondDetailsResponse?.paymentSchedule?.length;

    const handleGoBack = () => router.back();

    const { loginModal, showDealInterestModal } = useContext(AuthContext);
    const { isLogged } = useAuthStore();

    const { investorId, userId } = useStoreSelector(useAuthStore, (state) => ({
        investorId: state?.user?.id,
        userId: state?.user?.userId,
    }));

    const [isHeaderVisible, setIsHeaderVisible] = useState(true);
    const [showPaySchedule, setShowPaySchedule] = useState(false);

    const bondDetails = useMemo(
        () => bondDetailsResponse,
        [bondDetailsResponse]
    );
    const opportunityId = bondDetails?.opportunityId;

    const handleScroll = useCallback(() => {
        const stickyHeader = document.getElementById('sticky-header');
        setIsHeaderVisible(stickyHeader?.classList.contains('slide-up'));
    }, []);

    const postDealInterest = async (issuer) => {
        const dealInterestPayload = {
            investorId: investorId !== userId ? investorId : null,
            opportunityId: opportunityId || null,
            userId: userId || investorId,
            isin: bondDetails?.isin,
        };
        const createInterestRecord = await createDealInterest(
            dealInterestPayload
        );

        if (isValidResponseEntity(createInterestRecord)) {
            const [_, modalDetails] = determineAction(
                false,
                null,
                isLogged,
                decodeURIComponent(issuer)
            );
            showDealInterestModal(
                {
                    pathname: '/opportunities',
                },
                modalDetails
            );
        }
    };

    const getButtonText = () => {
        const isLiveDeal = !!opportunityId;
        const isFirstTimeUser = !isLogged;

        if (isFirstTimeUser) {
            return isLiveDeal ? texts?.LoginToInvest : texts?.requestForQuote;
        } else {
            return isLiveDeal ? texts?.SeeMoreDetails : texts?.requestForQuote;
        }
    };

    const handleOnClick = useCallback(async () => {
        const currentPath = router.asPath.split('?')[0];
        const issuer = encodeURIComponent(bondDetails?.title);
        const currentScrollPosition = window.scrollY;
        if (!isLogged) {
            triggerCustomGaEvent(
                activeOpportunityListingGaEventsName.CLICKED_ACTIVE_OPPORTUNITY_LOGIN_CTA,
                {
                    opportunityId,
                    isin: bondDetails?.isin,
                    referralCode: getReferralCode(),
                }
            );
            logEvent({
                eventName:
                    moengageEventConstants.CLICKED_ACTIVE_OPPORTUNITY_LOGIN_CTA,
                parameters: {
                    [moengageEventArgsConstants.OPPORTUNITY_ID]: opportunityId,
                    [moengageEventArgsConstants.ISIN]: bondDetails?.isin,
                    [moengageEventArgsConstants.REFERRAL_CODE]:
                        getReferralCode(),
                },
            });
        }

        if (opportunityId) {
            const redirectUrl = `/opportunity/${opportunityId}`;

            await router.replace(
                {
                    pathname: currentPath,
                    query: {
                        interestedIsin: bondDetails?.isin,
                        issuer,
                        redirect: redirectUrl,
                        opportunityId,
                    },
                },
                undefined,
                { shallow: true }
            );

            // Store current scroll position right after the URL change & before showing login modal
            window.sessionStorage.setItem(
                'scrollPosition',
                currentScrollPosition
            );

            if (isLogged) {
                router.push(`/opportunity/${opportunityId}`);
                return;
            }
        } else {
            await router.replace(
                {
                    pathname: currentPath,
                    query: {
                        interestedIsin: bondDetails?.isin,
                        issuer,
                    },
                },
                undefined,
                { shallow: true }
            );
            // Store current scroll position right after the URL change & before showing login modal
            window.sessionStorage.setItem(
                'scrollPosition',
                currentScrollPosition
            );
            if (isLogged) {
                return postDealInterest(issuer);
            }
        }

        loginModal();
    }, [bondDetails, opportunityId, isLogged, router, loginModal]);

    useEffect(() => {
        if (bondDetails) {
            logEvent({
                eventName: moengageEventConstants.VIEWED_BOND_DIRECTORY_PAGE,
                parameters: {
                    [moengageEventArgsConstants.ISIN]: bondDetails?.isin,
                    [moengageEventArgsConstants.ISSUER_NAME]:
                        bondDetails?.issuer,
                },
            });
        }
    }, [bondDetails]);

    useEffect(() => {
        if (isFromGoogle !== null) {
            triggerCustomGaEvent(
                isFromGoogle
                    ? bondDirectoryGaEventsName.VIEWED_BOND_DETAILS_FROM_LANDING_PAGE
                    : bondDirectoryGaEventsName.VIEWED_BOND_DETAILS_PAGE,
                {
                    isin: bondDetails?.isin,
                    issuer_name: bondDetails?.issuer,
                }
            );
        }
    }, [isFromGoogle, bondDetails]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const tabs = [
        {
            label: 'Snapshot',
            tabref: 'tab1',
            id: 'snapshot',
            children: (
                <div
                    className={cn('', {
                        'mt-2': opportunityId && width >= 1280,
                    })}
                >
                    <KeyAttributes keyAttributes={bondDetails?.keyAttributes} />
                    <DealDetails dealDetails={bondDetails?.dealDetails} />
                </div>
            ),
            show: true,
            tabId: 'snapshot',
        },
        {
            label: 'Risk Assessment',
            tabref: 'tab2',
            id: 'riskAssessment',
            children: (
                <div
                    className={cn('mt-2', {
                        'mt-2': opportunityId && width >= 1280,
                    })}
                >
                    <RiskAssessment riskDetails={bondDetails?.riskDetails} />
                </div>
            ),
            show: bondDetails?.riskDetails?.length > 0,
            tabId: 'riskAssessment',
        },
        {
            label: 'Key Investment Highlights',
            tabref: 'tab3',
            id: 'keyInvestmentHighlights',
            children: (
                <div className="mt-6">
                    <KeyInvestmentHighlightsMask
                        onButtonClick={handleOnClick}
                    />
                    <PricingMasked />
                </div>
            ),
            show: opportunityId && !isLogged,
            tabId: 'keyInvestmentHighlights',
        },
        {
            label: 'Documents',
            tabref: 'tab4',
            id: 'documents',
            children: (
                <div className="mt-4">
                    <DocumentsMasked />
                </div>
            ),
            show: opportunityId && !isLogged,
            tabId: 'documents',
        },
    ];

    return (
        <section className="relative flex flex-col min-h-screen pt-16 bg-opportunities-gradient">
            <OpportunityDetailsHeader
                title={title}
                tags={tags}
                details={details}
                logo={logo}
                onBack={handleGoBack}
                showPaymentSchedule={hasPaymentSchedule}
                onViewPaymentSchedule={() => setShowPaySchedule(true)}
                viewPaymentScheduleText={texts?.viewRepaymentSchedule}
            />
            <ListingWrapper
                className="w-full min-h-screen px-4 py-0 md:px-0 md:pb-0 md:pt-0 xl:pr-6 bg-basicWhite"
                wrapperClassName={cn(
                    'flex xl:flex-row xl:justify-between mx-auto w-full max-w-[1360px]'
                )}
                tag="main"
            >
                <section
                    className={cn(
                        'flex flex-col w-full pb-10 mx-auto xl:flex-row xl:gap-16 xl:items-start xl:pl-6',
                        {
                            'xl:pl-0 xl:pb-0': opportunityId,
                        }
                    )}
                >
                    <div
                        className={cn(
                            'flex-1 flex flex-col gap-6 xl:gap-8 w-full',
                            {
                                'xl:max-w-[744px]': !opportunityId,
                            }
                        )}
                    >
                        {opportunityId && width >= 1280 ? (
                            <ScrollableTabs
                                extraComponentAtEnd={
                                    <div className="mt-6 mb-16 xl:mt-8">
                                        <ContactDetails
                                            contactDetails={
                                                bondDetails?.contactDetails
                                            }
                                        />
                                    </div>
                                }
                                className="lg:w-fit lg:max-w-[206px] mb-6"
                            >
                                {tabs
                                    .filter((each) => each.show)
                                    .map((tab) => (
                                        <TabSection
                                            key={tab.id}
                                            label={tab.label}
                                            tabref={tab.tabref}
                                            id={tab.id}
                                            gtmtag={tab.gtmtag}
                                            children={tab.children}
                                        />
                                    ))}
                            </ScrollableTabs>
                        ) : (
                            <>
                                <KeyAttributes
                                    keyAttributes={bondDetails?.keyAttributes}
                                />
                                <DealDetails
                                    dealDetails={bondDetails?.dealDetails}
                                />
                                {bondDetails?.riskDetails?.length > 0 && (
                                    <RiskAssessment
                                        riskDetails={bondDetails?.riskDetails}
                                    />
                                )}
                                {opportunityId && !isLogged ? (
                                    <div className="relative h-fit">
                                        <div className="relative">
                                            <div
                                                className={cn(
                                                    'sticky top-40 z-[1] w-full mb-6 transition-all duration-300 ease-in-out xl:hidden h-[130px]'
                                                )}
                                            >
                                                <LoginCTA
                                                    title={texts.LearnMore}
                                                    description={
                                                        texts.LoginNowToDive
                                                    }
                                                    buttonText={texts.LoginNow}
                                                    onButtonClick={
                                                        handleOnClick
                                                    }
                                                />
                                            </div>

                                            <KeyInvestmentHighlightsMask
                                                onButtonClick={handleOnClick}
                                            />
                                            <PricingMasked />
                                            <DocumentsMasked />
                                        </div>
                                    </div>
                                ) : null}
                                <ContactDetails
                                    contactDetails={bondDetails?.contactDetails}
                                />
                            </>
                        )}
                    </div>
                    <aside
                        className={cn(
                            'border border-gray-300 rounded-xl xl:mb-6 overflow-hidden bg-basicWhite mt-6 top-20 min-w-[340px] sticky transition-all hidden xl:block',
                            { 'top-4': isHeaderVisible }
                        )}
                    >
                        {opportunityId && (
                            <PopularTag
                                type="liveNow"
                                className="mx-auto px-6"
                            />
                        )}
                        <div
                            className={cn('flex flex-col gap-3 px-4 pt-4', {
                                'pb-4': !bondDetails?.paymentSchedule?.length,
                            })}
                        >
                            <div className="flex justify-between gap-4">
                                <Text
                                    content={texts?.faceValue}
                                    className="text-gray-500 p4-medium"
                                />
                                <Text
                                    content={
                                        bondDetails?.faceValue
                                            ? `₹ ${getDecimalNumberWithComma(
                                                  bondDetails?.faceValue,
                                                  2
                                              )}`
                                            : '-'
                                    }
                                    className="text-gray-900 p4-bold"
                                />
                            </div>
                            {opportunityId && (
                                <div
                                    className={cn(
                                        'flex justify-between gap-4',
                                        {
                                            'border-b border-gray-300 pb-4':
                                                bondDetails?.paymentSchedule
                                                    ?.length,
                                        }
                                    )}
                                >
                                    <Text
                                        content={texts?.pricePerUnit}
                                        className="text-gray-500 p4-medium"
                                    />
                                    <Text
                                        content={
                                            !isNaN(
                                                bondDetails?.dailyDebenturePrice
                                            ) &&
                                            bondDetails?.dailyDebenturePrice
                                                ? `₹ ${getDecimalNumberWithComma(
                                                      bondDetails?.dailyDebenturePrice,
                                                      2
                                                  )}`
                                                : '-'
                                        }
                                        className="text-gray-900 p4-bold"
                                    />
                                </div>
                            )}
                        </div>
                        {bondDetails?.paymentSchedule?.length ? (
                            <div
                                className="flex items-center justify-center w-full p-4 cursor-pointer"
                                onClick={() => setShowPaySchedule(true)}
                            >
                                <Image src={'/images/Show.svg'} alt="show" />
                                <Text
                                    content={texts?.repaymentSchedule}
                                    className="pl-1 p4-medium text-primary-500"
                                />
                            </div>
                        ) : null}
                        <div className="px-4 pb-4">
                            <Button
                                onClick={handleOnClick}
                                buttonSize={buttonSizes.REGULAR}
                                buttonText={getButtonText()}
                                buttonType={buttonTypes.PRIMARY}
                                widthClass="w-full "
                            />
                        </div>
                    </aside>
                </section>
            </ListingWrapper>
            <div className="sticky bottom-0 z-10 flex flex-col w-full border-t border-gray-300 shadow-md bg-basicWhite xl:hidden gap">
                {opportunityId && (
                    <PopularTag type="liveNow" className="mx-auto px-6" />
                )}
                <div className="p-4">
                    <Button
                        onClick={handleOnClick}
                        buttonSize={buttonSizes.REGULAR}
                        buttonText={getButtonText()}
                        buttonType={buttonTypes.PRIMARY}
                        widthClass="w-full"
                    />
                </div>
            </div>

            {!opportunityId ? (
                <BondDirectoryOpportunityCarousel
                    className={'bg-bgColor-50 pt-5 md:pt-10'}
                />
            ) : null}
            {showPaySchedule && (
                <Modal
                    show={showPaySchedule}
                    onClose={() => setShowPaySchedule(false)}
                    width="md:w-2/3 md:max-w-screen-sm max-w-[95%]"
                    height="h-auto max-h-122 !p-0"
                    label={
                        <Text
                            content={texts?.repaymentScheduleTitle}
                            isInnerHtml
                            htmlTag="span"
                        />
                    }
                >
                    <RepaymentScheduleModal
                        paymentSchedule={bondDetails?.paymentSchedule}
                    />
                </Modal>
            )}
        </section>
    );
};

export default BondDetailsView;
